import * as PIXI from "pixi.js";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";
import {
  evaluatePath,
  computeArc,
  computeDirectionalArc,
  EASING_FNS,
} from "../../Engine/Collection/BezierPath";
import {
  captureAnimationState,
  applyAnimationState,
  createTravelingSpine,
  findAnimation,
} from "../../Engine/Collection/SpineCloner";
import FXManager from "../../Engine/Effects/FXManager";
import { createValueText, injectIntoSlot } from "../../Engine/Collection/ValueTextInjector";
import { orchestrate } from "../../Engine/Collection/TravelOrchestrator";

// ─── CoinCollectAnimator ─────────────────────────────────────────────────────

/**
 * CoinCollectAnimator
 *
 * Animates spine-based coin/jackpot instances along curved paths from their
 * grid positions to the royal symbol on the middle reel.
 *
 * Composes Engine/Collection primitives:
 *   - TravelOrchestrator: stagger/cancel/promise queue
 *   - BezierPath: arc computation + evaluation
 *   - SpineCloner: frame-matched spine creation
 *   - FXManager: RevoltFX particle trails
 *   - ValueTextInjector: text creation + bone slot injection
 *
 * Flow per coin:
 *   1. Capture overlay spine's current animation frame
 *   2. Hide overlay spine at original position
 *   3. Create a fresh Spine instance with the correct skin
 *   4. Apply idle animation at the exact captured frame (seamless clone)
 *   5. Queue "collect" animation to play immediately after
 *   6. Move the spine along a quadratic Bézier arc
 *   7. Destroy the spine on arrival
 *
 * The spine handles its own expand/shrink visuals via animation.
 * The animator only drives position and alpha fade.
 */
class CoinCollectAnimator {
  /**
   * @param {PIXI.Container} collectionContainer - Parent container for traveling spines
   * @param {number} symbolWidth - Current symbol cell width
   * @param {number} symbolHeight - Current symbol cell height
   */
  constructor(collectionContainer, symbolWidth, symbolHeight) {
    this.collectionContainer = collectionContainer;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this._activeTravelers = [];
    this._cancelled = false;
    this._cancelOrchestrator = null;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Animate a list of coins sequentially (left→right) to the royal position.
   *
   * @param {Array<object>} coins - Coin data with spine info and positions
   * @param {{x: number, y: number}} royalPosition - Destination position
   * @param {object} options
   * @returns {Promise<void>}
   */
  collectCoins(coins, royalPosition, options = {}) {
    const { onCoinCollected, onAllComplete, speedConfig } = options;
    this._cancelled = false;

    const config = FEATURES_CONFIG.ROYAL_COLLECT.COLLECTION;
    // Use speed-dependent travel duration if explicitly set, otherwise fall back to flat config.
    // null = intentional "use spine's natural duration" (passed through to _animateSingleItem).
    const travelDuration = (speedConfig?.COIN_TRAVEL_DURATION !== undefined)
      ? speedConfig.COIN_TRAVEL_DURATION
      : config.COIN_TRAVEL_DURATION;

    const staggerRatio = speedConfig?.STAGGER_RATIO ?? 0.5;

    const { promise, cancel } = orchestrate(
      coins,
      (coin, index, onComplete) => {
        this._animateSingleItem(coin, royalPosition, config, () => {
          onComplete();
        }, travelDuration);
      },
      {
        staggerRatio,
        travelDuration,
        onItemComplete: (coin, index) => onCoinCollected?.(coin.symbolId, index),
        onAllComplete,
      },
    );

    this._cancelOrchestrator = cancel;
    return promise;
  }

  /**
   * Animate royal symbols from their grid positions to the counter.
   * Same travel infrastructure as coins, with directional arcs.
   *
   * @param {Array<object>} royals - Royal data with spine info, positions, and arcDirection
   * @param {{x: number, y: number}} counterPosition - Destination (respin counter center)
   * @param {object} options - { onRoyalCollected, onAllComplete, speedConfig }
   * @returns {Promise<void>}
   */
  collectRoyals(royals, counterPosition, options = {}) {
    const { onRoyalCollected, onAllComplete, speedConfig } = options;
    this._cancelled = false;

    const config = FEATURES_CONFIG.ROYAL_COLLECT.COLLECTION;
    const travelDuration = (speedConfig?.COIN_TRAVEL_DURATION !== undefined)
      ? speedConfig.COIN_TRAVEL_DURATION
      : config.COIN_TRAVEL_DURATION;

    const staggerRatio = speedConfig?.STAGGER_RATIO ?? 0.5;

    const { promise, cancel } = orchestrate(
      royals,
      (royal, index, onComplete) => {
        this._animateSingleItem(royal, counterPosition, config, () => {
          onComplete();
        }, travelDuration);
      },
      {
        staggerRatio,
        travelDuration,
        onItemComplete: (royal, index) => onRoyalCollected?.(royal.symbolId, index),
        onAllComplete,
      },
    );

    this._cancelOrchestrator = cancel;
    return promise;
  }

  cancel() {
    this._cancelled = true;
    this._cancelOrchestrator?.();
    for (const traveler of this._activeTravelers) {
      if (traveler.tickerFn) {
        PIXI.Ticker.shared.remove(traveler.tickerFn);
      }
      // Kill trail immediately on cancel (no fade-out)
      if (traveler.trailEmitter) {
        FXManager.killTrail(traveler.trailEmitter);
        traveler.trailEmitter = null;
      }
      if (traveler.container) {
        traveler.container.destroy({ children: true });
      }
    }
    this._activeTravelers = [];
  }

  resize(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
  }

  destroy() {
    this.cancel();
    this.collectionContainer = null;
  }

  // ── Private: Single Item Animation ─────────────────────────────────────────

  /**
   * @param {object} item - { symbolId, spineName, skinName, valueTextData, startPos, hideOriginal, overlayController, arcDirection }
   * @param {{x,y}} destination
   * @param {object} config
   * @param {function} onComplete
   * @param {number} overrideDuration - Speed-dependent travel duration override
   */
  _animateSingleItem(item, destination, config, onComplete, overrideDuration) {
    if (!this.collectionContainer) {
      onComplete?.();
      return;
    }

    // Step 1: Capture overlay spine's current animation state BEFORE hiding
    const { animName: sourceAnimName, trackTime: sourceTrackTime } =
      captureAnimationState(item.overlayController);

    // Step 2: Now hide the overlay spine
    item.hideOriginal?.();

    // Step 3: Create a traveling container with a fresh Spine instance
    const itemContainer = new PIXI.Container();
    itemContainer.name = `collect_item_${item.symbolId}`;
    itemContainer.position.set(item.startPos.x, item.startPos.y);
    itemContainer.alpha = 1;

    const spine = createTravelingSpine(item.spineName, item.skinName);
    if (!spine) {
      console.warn('[CoinCollectAnimator] Failed to create spine for', item.spineName, item.skinName);
      itemContainer.destroy();
      onComplete?.();
      return;
    }

    // Scale spine to match current symbol dimensions
    const spineScale = this.symbolHeight / Settings.SYMBOL_HEIGHT;
    spine.scale.set(spineScale);
    spine.autoUpdate = false; // Prevent reset-pose flash on first frame
    itemContainer.addChild(spine);

    // Step 4: Determine travel duration
    // All collectibles use the same canonical duration from config (or speed override).
    // This ensures uniform travel speed regardless of individual spine animation lengths.
    const collectAnim = findAnimation(spine, "collect");
    const duration = overrideDuration || config.COIN_TRAVEL_DURATION;

    // Step 5: Apply animation — frame-matched from overlay or fallback to idle
    applyAnimationState(spine, sourceAnimName, sourceTrackTime, {
      followUpAnim: collectAnim,
      travelDuration: duration,
    });

    // Now it's safe to auto-update (animation is already applied at correct frame)
    spine.autoUpdate = true;

    // Step 6: Inject value text into spine's txt_slot_value slot
    if (item.valueTextData) {
      const valueTextContainer = createValueText(
        item.valueTextData,
        spine,
        this.symbolWidth,
      );
      injectIntoSlot(spine, "txt_slot_value", valueTextContainer);
    }

    this.collectionContainer.addChild(itemContainer);

    // Step 7: Start trail effect (RevoltFX) with coin-specific color
    let trailEmitter = null;
    if (FXManager.isReady) {
      const trailScale = this.symbolHeight / Settings.SYMBOL_HEIGHT;
      trailEmitter = FXManager.emitLightning(
        this.collectionContainer,
        itemContainer,
        0, // rotation — placeholder (Royal Collect disabled)
        trailScale * 0.55,
      );
    }

    // Step 8: Animate position along Bézier curve
    // Normalize positions to symbol units so they adapt on resize.
    const captureW = this.symbolWidth;
    const captureH = this.symbolHeight;
    const normStart = { x: item.startPos.x / captureW, y: item.startPos.y / captureH };
    const normEnd = { x: destination.x / captureW, y: destination.y / captureH };

    const easingFn = EASING_FNS.easeInOut;
    let elapsed = 0;

    const travelerData = { container: itemContainer, tickerFn: null, trailEmitter };

    travelerData.tickerFn = () => {
      elapsed += PIXI.Ticker.shared.deltaMS;
      const rawT = Math.min(elapsed / duration, 1);
      const easedT = easingFn(rawT);

      // Recompute actual positions from normalized coords × live dimensions
      const liveW = this.symbolWidth;
      const liveH = this.symbolHeight;
      const start = { x: normStart.x * liveW, y: normStart.y * liveH };
      const end = { x: normEnd.x * liveW, y: normEnd.y * liveH };

      const pathData = item.arcDirection
        ? computeDirectionalArc(
            start, end, liveW, liveH, item.arcDirection,
            config.ROYAL_ARC_LATERAL_OFFSET || 0.5,
          )
        : computeArc(start, end, liveH);

      // Position along curve (spine animation handles scale/visuals)
      const pos = evaluatePath(pathData, easedT);
      itemContainer.position.set(pos.x, pos.y);

      // Complete
      if (rawT >= 1) {
        PIXI.Ticker.shared.remove(travelerData.tickerFn);
        travelerData.tickerFn = null;

        const idx = this._activeTravelers.indexOf(travelerData);
        if (idx !== -1) this._activeTravelers.splice(idx, 1);

        // Stop trail — existing particles fade out naturally
        if (trailEmitter) {
          FXManager.stopTrail(trailEmitter);
          trailEmitter = null;
        }

        // Destroy the container, spine, and all children
        itemContainer.destroy({ children: true });
        onComplete?.();
      }
    };

    this._activeTravelers.push(travelerData);
    PIXI.Ticker.shared.add(travelerData.tickerFn);
  }
}

export default CoinCollectAnimator;
